import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Users, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { CampaignHistoryModal } from './CampaignHistoryModal';

// Define types locally
interface BrandAthlete {
  id: string;
  name: string;
  sport: string;
  avgEngagementRate: number;
  campaignsCount: number;
  followersCount: number;
  totalReach: number;
  paymentsDue: number;
  lastCampaignDate: string;
  athleteScore: number;
  tier: string;
  profileId: string;
  avatarUrl?: string;
}

interface AthleteEngagementGridProps {
  athletes: BrandAthlete[];
  onViewHistory: (athlete: BrandAthlete) => void;
}

export const AthleteEngagementGrid: React.FC<AthleteEngagementGridProps> = ({ athletes, onViewHistory }) => {
  const [selectedAthlete, setSelectedAthlete] = useState<{ id: string; name: string } | null>(null);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(num);
  };

  const getSportColor = (sport: string) => {
    const colors = {
      'Baseball': 'bg-amber-500/10 text-amber-600 border-amber-500/20',
      'Tennis': 'bg-green-500/10 text-green-600 border-green-500/20',
      'Football': 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      'Basketball': 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      'Soccer': 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
      'Formula 1': 'bg-red-500/10 text-red-600 border-red-500/20'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-500/10 text-gray-600 border-gray-500/20';
  };


  return (
    <Card className="deal-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          Active Partnerships ({athletes.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {athletes.map((athlete) => {
            
            return (
              <Card key={athlete.id} className="border border-border hover:bg-muted/30 transition-all duration-200 cursor-pointer group">
                <CardContent className="p-4">
                  <div className="space-y-4">
                    {/* Athlete Header */}
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={athlete.avatarUrl} alt={athlete.name} />
                        <AvatarFallback className="bg-gray-500/20 text-gray-700 dark:text-gray-300 text-lg font-semibold">
                          {athlete.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-foreground text-sm line-clamp-1">{athlete.name}</h3>
                        <Badge variant="outline" className={`${getSportColor(athlete.sport)} text-xs mt-1`}>
                          {athlete.sport}
                        </Badge>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <div className="text-muted-foreground">Campaigns</div>
                        <div className="font-semibold text-sm">{athlete.campaignsCount}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Followers</div>
                        <div className="font-semibold text-sm">{formatNumber(athlete.followersCount)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Total Reach</div>
                        <div className="font-semibold text-sm text-primary">{formatNumber(athlete.totalReach)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Payments Due</div>
                        <div className="font-semibold text-sm text-amber-600">{formatCurrency(athlete.paymentsDue)}</div>
                      </div>
                    </div>

                    {/* Last Campaign */}
                    <div className="flex items-center gap-1 text-xs text-muted-foreground border-t border-border pt-3">
                      <Calendar className="h-3 w-3" />
                      <span>Last campaign: {new Date(athlete.lastCampaignDate).toLocaleDateString()}</span>
                    </div>

                    {/* Action Button */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full text-xs group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      onClick={() => {
                        setSelectedAthlete({ id: athlete.profileId, name: athlete.name });
                        setIsHistoryModalOpen(true);
                      }}
                    >
                      View Campaign History
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {athletes.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No active partnerships found</p>
            <p className="text-sm text-muted-foreground mt-1">Start creating campaigns to see athlete engagement data</p>
          </div>
        )}

        {/* Campaign History Modal */}
        {selectedAthlete && (
          <CampaignHistoryModal
            athleteId={selectedAthlete.id}
            athleteName={selectedAthlete.name}
            isOpen={isHistoryModalOpen}
            onClose={() => {
              setIsHistoryModalOpen(false);
              setSelectedAthlete(null);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};